import React, { useContext, useState } from 'react';
import Cookies from 'js-cookie';

import { InputError } from '@riac/design-system';
import { formatCurrency } from 'src/lib/catalog';
import { IAuctionItem } from 'src/pages/CatalogPage/CatalogPage.i';
import { TAuctionTypeID } from 'src/pages/CatalogPage/Catalog.i';
import { Checkbox } from 'src/components/Input/CheckboxInput/CheckboxInput';
import { Label } from '@riac/design-system';
import { AccordionItem } from '@riac/design-system';
import { InvertedRadioOption } from 'src/components/Input/RadioInput/InvertedRadioInput';
import {
  BidPlus,
  PlaceBidModal,
  SecondaryP,
} from 'src/components/PlaceBid/PlaceBid.style';
import { ModalContentArea } from '@riac/design-system';
import { Input } from '@riac/design-system';
import { useFetch } from 'src/lib/FetchContext';
import { getNextPrice, helperCheckIncrement } from '@riac/core';
import { BoxButton } from '@riac/design-system';
import { AccountHeaderContext } from 'src/lib/AccountHeaderContext';
import { OverlayContainer } from '@react-aria/overlays';
import { BiddingContext } from 'src/lib/BiddingContext';

interface IBidModal {
  closeModal(): void;

  item: IAuctionItem;
  auctionTypeId: TAuctionTypeID;

  bidUpdatedCallback(bid?: number, bidPlus?: number): void;

  type: 'sealed' | 'phone' | 'increment';
  page: 'cart' | 'catalog' | 'detail' | 'my-bids';
}

const BidSuccess = ({ dontShow, setDontShow, closeModal }): JSX.Element => {
  return (
    <div>
      <br/>
      <p>Your bid has been saved to your Bid Cart!</p>
      <br/>
      <p>
        When you are finished adding items to your bid cart you must submit these bids to be
        accepted.
      </p>
      <br/>
      <p>
        Submit your bids in the menu item ‘Cart-Unsubmitted Bids’, then click the button ‘Submit
        Auction Bids’
      </p>
      <br/>
      <Label>
        <Checkbox
          id="doNotShow"
          type="checkbox"
          checked={dontShow}
          onChange={() => setDontShow((show: boolean) => !show)}
        />
        Do not show this message again
      </Label>
      <BoxButton
        onPress={() => {
          if(dontShow) Cookies.set('hideBidPlacedModal', 'true');
          closeModal();
        }}
      >
        OK
      </BoxButton>
    </div>
  );
};

const BidInputArea = ({
  bidType,
  auctionType,
  bidAmount,
  setBidAmount,
  checkSubmit,
  plus,
  setPlus,
}): JSX.Element => {
  return (
    <div>
      <Input
        label={`${bidType === 'phone' ? 'Cover Me ' : ''}Bid Amount: $`}
        value={bidAmount}
        inverted
        onChange={setBidAmount}
        onKeyDown={checkSubmit}
      />
      {auctionType != TIMED_AUCTION_TYPE_ID && (
        <BidPlus>
          Bid Plus:
          <InvertedRadioOption
            name="bidPlus"
            value={0}
            checked={plus === 0}
            onChange={() => setPlus(0)}
          >
            No Plus
          </InvertedRadioOption>
          <InvertedRadioOption
            name="bidPlus"
            value={1}
            checked={plus === 1}
            onChange={() => setPlus(1)}
          >
            +1
          </InvertedRadioOption>
          <InvertedRadioOption
            name="bidPlus"
            value={2}
            checked={plus === 2}
            onChange={() => setPlus(2)}
          >
            +2
          </InvertedRadioOption>
        </BidPlus>
      )}
    </div>
  );
};

export const BidModal = (props: IBidModal): JSX.Element => {
  const { fetch } = useFetch();
  const { updateHeaderCounts } = useContext(AccountHeaderContext);
  const [doNotShowSuccess, setDoNotShowSuccess] = useState(() => {
    try {
      return Cookies.get('hideBidPlacedModal') === 'true';
    } catch (e) {
      return false;
    }
  });
  const [highEstAgreed, setHighEstAgreed] = useState(false);
  const [bidError, setBidError] = useState('');
  const [bidPlus, setBidPlus] = useState(0);
  const [bidSuccess, setBidSuccess] = useState(false);
  const [userBidAmount, setUserBidAmount] = useState('');
  const [userRekeyBidAmount, setUserRekeyBidAmount] = useState('');
  const [exceedsHighEstWarning, setExceedsHighEstWarning] = useState(false);
  const { placeTimedAuctionBid } = useContext(BiddingContext);

  const keyBidTwice = props.auctionTypeId === TIMED_AUCTION_TYPE_ID
    && (parseInt(userBidAmount) || 0) >= 100
    && (parseInt(userBidAmount) || 0) >= (props.item.CurrentBid || props.item.StartingBid) * 2;

  function bidAmountNumbersOnly(e: string): void {
    setUserBidAmount(e.replace(/[^0-9.]/g, ''));
  }

  function checkForSealedBidSubmit(e: any): void {
    if(e.key === 'Enter') {
      e.preventDefault();
      submitBid().then();
    }
  }

  async function submitBid(): Promise<void> {
    setBidError('');
    setExceedsHighEstWarning(false);
    let bid = parseInt(userBidAmount);
    const bidFloat = parseFloat(userBidAmount);
    if(props.type !== 'phone' && bidFloat !== bid) {
      setBidError('Please enter a valid bid amount');
      return;
    }
    if(keyBidTwice) {
      const rekeyBid = parseInt(userRekeyBidAmount);
      if(bid !== rekeyBid) {
        setBidError('Your bid amounts must match');
        return;
      }
    }
    // skip bid amount verification if using phone and no cover me bid
    if(!(props.type === 'phone' && !userBidAmount)) {
      if(!userBidAmount || !userBidAmount.length || isNaN(bid)) {
        setBidError('Please enter a bid amount');
        return;
      }
      const increment = helperCheckIncrement(bid);
      if(increment[0] !== bid) {
        setBidError(`Bid increment is not valid. Try ${increment[0]} or ${increment[1]}.`);
        return;
      }
      // Per Kevin and the Jack Anderson Rule, NO LOWERING BIDS without customer service.
      if(props.page !== 'cart' && props.item.Price && bid <= props.item.Price) {
        setBidError(`Please contact customer service if you wish to lower your ${props.type === 'phone' ? 'cover me amount' : 'bid'}.`);
        return;
      }
      // This bid severely exceeds the High Estimate
      if(props.auctionTypeId !== TIMED_AUCTION_TYPE_ID && bid >= props.item.HighEst * 2) {
        setExceedsHighEstWarning(true);
        if(!highEstAgreed) {
          return;
        }
      }
      if(bid < minBid()) {
        setBidError('Bid is lower than the minimum bid amount');
        return;
      }
    } else {
      bid = 0;
    }
    if(props.auctionTypeId === TIMED_AUCTION_TYPE_ID) {
      placeTimedAuctionBid(props.item, bid).then(
        (bidResponse): void => {
          props.closeModal();
          if(!bidResponse.success) {
            setBidError(bidResponse.errorMessage || 'An error occurred');
          }
          updateHeaderCounts().then();
          if(props.bidUpdatedCallback) {
            props.bidUpdatedCallback(bid, 0);
          }
        }, (): void => {
          setBidError('An unknown error occurred');
        });
    } else {
      const bidReq = await fetch('/api/bids/placebid', {
        credentials: 'include',
        method: 'POST',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          item: props.item,
          thebid: bid,
          plus: bidPlus,
          bidtype: props.type === 'phone' ? 2 : 1,
        }),
      });
      const bidRes = await bidReq.json();
      switch (bidRes) {
        case 'OK':
        case 'UPD':
          if(props.bidUpdatedCallback) {
            props.bidUpdatedCallback(bid, bidPlus);
          }
          updateHeaderCounts().then();
          doNotShowSuccess ? props.closeModal() : setBidSuccess(true);
          break;
        case 'mnm':
          setBidError(`Minimum bid amount not met, $${minBid()} minimum.`);
          break;
        case 'bii':
          setBidError('Bid increment is incorrect.');
          break;
        case 'Not Authorized':
          setBidError('Your session has expired. Please login to your account and try again.');
          break;
        case 'nub':
          setBidError(`Please contact customer service if you wish to lower your ${props.type === 'phone' ? 'cover me amount' : 'bid'}.`);
          break;
        default:
          setBidError(`An unknown server Error has occurred. Code: ${reportClientError(JSON.stringify(bidRes))}.`
            + ' If you need further assistance, please contact customer service.');
      }
    }
  }

  function minBid(): number {
    if(props.auctionTypeId === TIMED_AUCTION_TYPE_ID) {
      return props.item.CurrentBid ? getNextPrice(props.item.CurrentBid) : props.item.StartingBid;
    }
    if(props.item.HLO === 'O') {
      return helperCheckIncrement(0.25 * props.item.LowEst)[1];
    } else {
      return helperCheckIncrement(0.4 * props.item.LowEst)[1];
    }
  }

  return (
    <OverlayContainer>
      <PlaceBidModal
        isOpen
        isDismissable
        showCloseButton
        title={`Place a ${props.type === 'phone'
          ? 'Telephone '
          : props.auctionTypeId == TIMED_AUCTION_TYPE_ID ? '' : 'Sealed '} Bid`}
        onClose={props.closeModal}
      >
        <ModalContentArea>
          {bidSuccess ? (
            <BidSuccess
              dontShow={doNotShowSuccess}
              setDontShow={setDoNotShowSuccess}
              closeModal={props.closeModal}
            />
          ) : (
            <>
              <p>Lot # {props.item.LotNumber}</p>
              {props.auctionTypeId != TIMED_AUCTION_TYPE_ID
                && <SecondaryP>
                  Est. Price: {formatCurrency(props.item.LowEst)} - {formatCurrency(props.item.HighEst)}
                </SecondaryP>
              }
              {props.auctionTypeId != TIMED_AUCTION_TYPE_ID
                && <p>
                  NOTICE: Our terms and conditions have changed effective 3/2/2022, including a changes to Buyer's
                  Premium, Fees, and Shipping. Please review the new terms and conditions before bidding.
                </p>
              }
              {props.type === 'phone' ? (
                <BidInputArea
                  bidType={props.type}
                  auctionType={props.auctionTypeId}
                  bidAmount={userBidAmount}
                  setBidAmount={bidAmountNumbersOnly}
                  checkSubmit={checkForSealedBidSubmit}
                  plus={bidPlus}
                  setPlus={setBidPlus}
                />
              ) : (
                <>
                  <BidInputArea
                    bidType={props.type}
                    auctionType={props.auctionTypeId}
                    bidAmount={userBidAmount}
                    setBidAmount={bidAmountNumbersOnly}
                    checkSubmit={checkForSealedBidSubmit}
                    plus={bidPlus}
                    setPlus={setBidPlus}
                  />
                  {keyBidTwice
                    && <Input
                      label="Verify Bid Amount: $"
                      value={userRekeyBidAmount}
                      inverted
                      onChange={(e) => setUserRekeyBidAmount(e.replace(/[^0-9.]/g, ''))}
                      onKeyDown={checkForSealedBidSubmit}
                    />
                  }
                </>
              )}
              <div>
                {props.auctionTypeId === TIMED_AUCTION_TYPE_ID && keyBidTwice
                  && <InputError error={`Your bid exceeds 200% of the current bid for this item.  
                    Are you sure you want to place this bid?`}/>
                }
                {exceedsHighEstWarning && (
                  <>
                    <>
                      <InputError
                        error={`Your bid meets or exceeds 200% of the HIGH estimate
                    for this item. Are your sure you want to place this bid?`}
                      />
                      <Label>
                        <Checkbox
                          id="highEstAgreedCheckboxModal"
                          type="checkbox"
                          checked={highEstAgreed}
                          onChange={() => setHighEstAgreed((agree) => !agree)}
                        />
                          Yes, I am sure I want to place this bid.
                      </Label>
                    </>
                  </>
                )}
              </div>
              <InputError error={bidError}/>
              <BoxButton
                onPress={submitBid}
                isDisabled={exceedsHighEstWarning && !highEstAgreed}
              >
                Save Bid
              </BoxButton>
              {props.type === 'phone' ? (
                <AccordionItem
                  heading={<span className="text-grey-25">What is a Telephone Bid?</span>}
                  content={
                    <SecondaryP>
                      You will be called by a representative during the auction and bid live via phone.
                      We do suggest leaving optional “Cover-Me Bids” in case you are not able to be
                      reached by telephone. In order to bid on a lot over the telephone at or near the
                      time it is being sold during the auction you must contact us in advance to make
                      arrangements. Generally if you contact us the day preceding the sale we will have
                      time to process your information.
                    </SecondaryP>
                  }
                />
              ) : null}
            </>
          )}
        </ModalContentArea>
      </PlaceBidModal>
    </OverlayContainer>
  );
};
